'use client';

// ============================================================================
// Fichiers d'une société émettrice : logo et CGV.
// ----------------------------------------------------------------------------
// POURQUOI CE FICHIER EXISTE.
//
// Le serveur enregistre le logo sous forme de chemin relatif
// « /uploads/brand-logos/... », qu'il ne sait servir que depuis api.vyndra.fr.
// Affiché tel quel dans le back-office (app.vyndra.fr), ce chemin donnait un
// HTTP 404 et l'icône d'image brisée. Toute adresse de fichier de marque doit
// donc passer par `resolveBrandAssetUrl`, qui la ramène sur notre domaine via
// le relais /api/brand-assets (voir la note détaillée dans ce relais).
//
// Second principe, imposé par la règle maison « aucune erreur avalée » : quand
// un logo ne se charge pas, on ne laisse JAMAIS l'icône d'image brisée du
// navigateur. On affiche un repli lisible et on écrit pourquoi.
// ============================================================================

import { useEffect, useRef, useState } from 'react';
import { WarningCircle } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';

// Traduction des adresses : module pur, verifiable isolement. Re-exporte ici
// pour que les appelants n'aient qu'un seul point d'entree a connaitre.
export {
  resolveBrandAssetUrl,
  isBrandAssetUrlUnusable,
  resoudreLogo,
  logoBrut,
  ordreDeRepli,
  decrireVariante,
  compterLogosDeposes,
  conseilsLogos,
  poseSelonRatio,
  VARIANTES_LOGO,
  // Forme réelle d'un fichier, et choix de variante par la LUMINANCE du fond.
  diagnostiquerForme,
  mesurerForme,
  nommerForme,
  fondEstSombre,
  variantePourFond,
  RATIO_CARRE_MIN,
  RATIO_CARRE_MAX,
  RATIO_LONGUEUR_MIN,
  // Le choix du logo par EMPLACEMENT (19/09/2026) : table, lecture du réglage,
  // et le point de décision unique logoPourEmplacement.
  EMPLACEMENTS_LOGO,
  decrireEmplacement,
  estEmplacementLogo,
  estReglageEmplacement,
  lireReglagesEmplacements,
  varianteDeLEmplacement,
  ordreDeRepliMemeFond,
  resoudreLogoMemeFond,
  logoPourEmplacement,
  composerVariante,
  formeDeVariante,
  fondDeVariante,
} from './brand-asset-utils';
export type {
  ConseilLogo,
  PoseLogo,
  VarianteLogo,
  FormeLogo,
  FondLogo,
  JeuLogos,
  LogoResolu,
  DescriptionVariante,
  DiagnosticForme,
  FormeMesuree,
  ChoixVariante,
  EmplacementLogo,
  ReglageEmplacement,
  ReglagesEmplacements,
  DescriptionEmplacement,
  LogoEmplacementResolu,
} from './brand-asset-utils';
import {
  resolveBrandAssetUrl,
  isBrandAssetUrlUnusable,
  resoudreLogo,
  logoPourEmplacement,
  poseSelonRatio,
  type EmplacementLogo,
  type JeuLogos,
  type PoseLogo,
  type ReglagesEmplacements,
  type VarianteLogo,
} from './brand-asset-utils';

/**
 * CE QU'ON MONTRE QUAND AUCUN FICHIER NE CONVIENT. Jamais une lettre inventée.
 *
 * DÉCIDÉ LE 21/09/2026, ET C'EST UNE CORRECTION, PAS UN GOÛT. Ce composant
 * retombait sur `nom.charAt(0)`, donc sur un « G » ou un « V » dessiné dans une
 * plaque de couleur. Mot pour mot le dirigeant, capture à l'appui : « il y a un
 * vrai logo dans les réglages en PNG, ce n'est pas pour que tu m'inventes je ne
 * sais quoi... même pour Vyndra tu m'inventes un V chelou, alors qu'on a un
 * logo propre. »
 *
 * Deux replis, et deux seulement :
 *   « nom »  : le nom de la marque EN TOUTES LETTRES, rogné par son cadre s'il
 *              ne tient pas. C'est le défaut.
 *   « rien » : rien du tout. Réservé aux pastilles minuscules qui ont DÉJÀ le
 *              nom écrit juste à côté : l'y remettre le ferait apparaître deux
 *              fois, et une plaque vide de 26 px ne dit rien de faux.
 */
export type ReplisMarque = 'nom' | 'rien';

/**
 * Suit l'échec de chargement d'une image, de façon FIABLE.
 *
 * POURQUOI CE N'EST PAS QU'UN onError. Vérifié au navigateur le 06/08/2026 :
 * la page est rendue côté serveur, le navigateur commence à charger l'image et
 * échoue AVANT que React n'ait fini son hydratation et attaché le gestionnaire.
 * L'évènement « error » est alors perdu, et l'écran restait bloqué sur l'icône
 * d'image brisée alors que le composant avait un repli tout prêt.
 *
 * On combine donc les deux : le gestionnaire pour les échecs à venir, et une
 * vérification de l'état RÉEL de l'image au montage pour ceux déjà survenus
 * (`complete` vrai avec une largeur naturelle nulle = échec).
 */
export function useImageEnEchec(adresse: string | null) {
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [enEchec, setEnEchec] = useState(false);

  useEffect(() => {
    const img = imgRef.current;
    setEnEchec(!!img && img.complete && img.naturalWidth === 0);
  }, [adresse]);

  return { imgRef, enEchec, signalerEchec: () => setEnEchec(true) };
}

/**
 * Forme RÉELLE du fichier affiché, et la façon de le poser qui en découle.
 *
 * POURQUOI C'EST ICI, ET PAS DANS UN ÉCRAN. Deux écrans refusaient un logo au
 * motif qu'il n'était pas carré, et retombaient sur un monogramme alors qu'un
 * fichier existait (tête du menu d'univers, carte du lanceur, 08/08/2026). La
 * décision « comment poser ce fichier » doit donc être prise UNE fois, au même
 * endroit que la règle de repli, et pas rebricolée par chaque emplacement.
 *
 * Même précaution que `useImageEnEchec` : l'image peut être déjà chargée avant
 * que React n'attache son gestionnaire, donc on lit aussi l'état RÉEL au montage.
 * Tant que la mesure n'est pas faite, on répond « blason », qui est le cas le
 * plus courant et ne fait jamais sauter la mise en page.
 */
export function useFormeNaturelle(adresse: string | null): {
  imgRef: React.RefObject<HTMLImageElement | null>;
  largeur: number | null;
  hauteur: number | null;
  ratio: number | null;
  pose: PoseLogo;
  relever: (el: HTMLImageElement) => void;
} {
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [taille, setTaille] = useState<{ l: number; h: number } | null>(null);

  useEffect(() => {
    const el = imgRef.current;
    if (el && el.complete && el.naturalWidth > 0 && el.naturalHeight > 0) {
      setTaille({ l: el.naturalWidth, h: el.naturalHeight });
    } else {
      setTaille(null);
    }
  }, [adresse]);

  const relever = (el: HTMLImageElement) => {
    if (el.naturalWidth > 0 && el.naturalHeight > 0) {
      setTaille({ l: el.naturalWidth, h: el.naturalHeight });
    }
  };

  const ratio = taille ? taille.l / taille.h : null;
  return {
    imgRef,
    largeur: taille?.l ?? null,
    hauteur: taille?.h ?? null,
    ratio,
    pose: poseSelonRatio(ratio),
    relever,
  };
}

interface BrandLogoProps {
  /**
   * Adresse telle que renvoyée par le serveur (chemin relatif /uploads/...).
   * Voie directe, conservée pour les appelants qui n'ont qu'une adresse.
   * Quand `jeu` est fourni, il prime : c'est lui qui applique la règle de repli.
   */
  url?: string | null | undefined;
  /**
   * Les quatre variantes de l'identité. Fourni avec `variante`, l'emplacement
   * demande le fichier ADAPTÉ à son fond et à sa forme, et retombe seul sur le
   * meilleur disponible quand cette variante n'a pas été déposée.
   */
  jeu?: JeuLogos | null;
  /** Variante attendue par cet emplacement. Défaut : en longueur, fond clair. */
  variante?: VarianteLogo;
  /**
   * L'EMPLACEMENT réel que cette pastille peint (voir EMPLACEMENTS_LOGO). Fourni,
   * il PRIME sur `variante` : le fichier vient du choix du dirigeant pour cet
   * endroit, sinon du défaut de la table, avec un repli qui ne change jamais de
   * fond. C'est le seul chemin qui respecte le réglage « Où chaque logo est
   * utilisé ».
   */
  emplacement?: EmplacementLogo;
  /** Le choix du dirigeant (`logoEmplacements` du serveur). */
  reglagesEmplacements?: ReglagesEmplacements | null;
  /** Nom de la société : alternative textuelle de l'image, et repli affiché. */
  nom: string;
  /**
   * Ce qui remplace le logo quand aucun fichier ne convient à cet emplacement.
   * Défaut : le nom en toutes lettres. Voir ReplisMarque.
   */
  repli?: ReplisMarque;
  /**
   * Hauteur imposée à l'image, en pixels, la largeur restant libre. C'est la
   * pose demandée pour les logotypes en longueur : on fixe la hauteur, jamais
   * la largeur, sinon un fichier large est écrasé ou un fichier haut est perdu.
   * Sans valeur, la taille vient des classes de l'appelant, comme avant.
   */
  hauteur?: number;
  /** Classes du conteneur. L'appelant fixe la taille. */
  className?: string;
  /** Classes de l'image elle-même. */
  imgClassName?: string;
  /** Remonte au parent l'explication du repli, pour l'afficher à côté. */
  onProbleme?: (motif: string | null) => void;
}

/**
 * Pastille de logo pour les listes et les en-têtes.
 *
 * Trois états, jamais d'image brisée :
 *  - logo affichable : l'image ;
 *  - aucun logo enregistré : le NOM de la société, en toutes lettres ;
 *  - logo enregistré mais introuvable ou illisible : le nom + un point
 *    d'alerte, et le motif remonté au parent via `onProbleme`.
 */
export function BrandLogo({
  url,
  jeu,
  variante = 'longueur-clair',
  emplacement,
  reglagesEmplacements,
  nom,
  repli = 'nom',
  hauteur,
  className,
  imgClassName,
  onProbleme,
}: BrandLogoProps) {
  // Le jeu complet prime : c'est lui qui sait choisir entre les quatre fichiers.
  // Et l'emplacement prime sur la variante : c'est lui qui porte le réglage.
  const resolu = jeu
    ? (emplacement
        ? logoPourEmplacement(jeu, emplacement, reglagesEmplacements)
        : resoudreLogo(jeu, variante))
    : null;
  const adresse = resolu ? resolu.url : resolveBrandAssetUrl(url);
  const inexploitable = resolu ? resolu.inexploitable : isBrandAssetUrlUnusable(url);
  // Un changement de logo réévalue l'état : le repli ne doit pas rester affiché
  // alors que le nouveau fichier est parfaitement valide.
  const { imgRef, enEchec, signalerEchec } = useImageEnEchec(adresse);

  const motif = inexploitable
    ? "L'adresse du logo enregistrée est inexploitable. Envoyez le fichier à nouveau."
    : enEchec
      ? "Le logo est introuvable sur le serveur. Il a peut-être été supprimé : envoyez-le à nouveau."
      : null;

  useEffect(() => { onProbleme?.(motif); }, [motif, onProbleme]);

  if (adresse && !enEchec) {
    return (
      // eslint-disable-next-line @next/next/no-img-element
      <img
        ref={imgRef}
        src={adresse}
        alt={`Logo ${nom}`}
        onError={signalerEchec}
        // Hauteur imposée, largeur libre : les proportions du fichier décident
        // de sa largeur, il n'est ni écrasé ni rogné.
        style={hauteur ? { height: hauteur, width: 'auto' } : undefined}
        className={cn('object-contain', imgClassName, className)}
      />
    );
  }

  // Rien à montrer, et le nom est déjà écrit à côté : on laisse la place vide
  // plutôt que d'inventer un dessin. Le motif reste remonté au parent.
  if (repli === 'rien' && !motif) return null;

  return (
    <span
      className={cn(
        'relative flex items-center justify-center overflow-hidden px-1 text-center font-bold leading-tight select-none',
        className,
      )}
      title={motif ?? `Aucun logo pour ${nom}`}
      aria-label={motif ?? `Aucun logo pour ${nom}`}
    >
      <span className="min-w-0 truncate">{repli === 'rien' ? '' : nom}</span>
      {motif && (
        <WarningCircle
          size={12}
          weight="fill"
          className="absolute -right-[3px] -top-[3px] text-[#FBBF24]"
          aria-hidden
        />
      )}
    </span>
  );
}

export type EtatApercuLogo = 'absent' | 'chargement' | 'pret' | 'echec';

/**
 * Charge un logo et le renvoie sous forme de data: URI.
 *
 * POURQUOI : l'aperçu du mail est rendu dans une iframe `sandbox=""`, donc avec
 * une origine opaque. Dans ce contexte la directive « img-src 'self' » de la
 * CSP ne s'applique pas de façon fiable selon les navigateurs, et une adresse
 * même same-origin peut être bloquée. Un data: URI, lui, est explicitement
 * autorisé par la CSP du site et ne demande aucun accès réseau à l'iframe.
 *
 * `actif` permet de ne payer ce chargement que lorsque l'aperçu est visible.
 */
export function useBrandAssetDataUrl(
  url: string | null | undefined,
  actif: boolean,
): { dataUrl: string | null; etat: EtatApercuLogo } {
  const adresse = resolveBrandAssetUrl(url);
  const [dataUrl, setDataUrl] = useState<string | null>(null);
  const [etat, setEtat] = useState<EtatApercuLogo>('absent');

  useEffect(() => {
    if (!actif || !adresse) {
      setDataUrl(null);
      setEtat(adresse ? 'chargement' : 'absent');
      return;
    }
    let annule = false;
    setEtat('chargement');
    (async () => {
      try {
        const reponse = await fetch(adresse, { cache: 'force-cache' });
        if (!reponse.ok) throw new Error(`HTTP ${reponse.status}`);
        const blob = await reponse.blob();
        const lu = await new Promise<string>((resolve, reject) => {
          const lecteur = new FileReader();
          lecteur.onload = () => resolve(String(lecteur.result));
          lecteur.onerror = () => reject(lecteur.error);
          lecteur.readAsDataURL(blob);
        });
        if (annule) return;
        setDataUrl(lu);
        setEtat('pret');
      } catch {
        // Jamais avalé : l'appelant affiche un repli et dit que le logo manque.
        if (annule) return;
        setDataUrl(null);
        setEtat('echec');
      }
    })();
    return () => { annule = true; };
  }, [adresse, actif]);

  return { dataUrl, etat };
}

interface BrandLogoBlocProps {
  url: string | null | undefined;
  nom: string;
}

/**
 * Grande zone d'aperçu du logo, pour l'écran d'édition. Contrairement à la
 * pastille, elle affiche le motif du problème EN TOUTES LETTRES : c'est là que
 * l'utilisateur doit comprendre ce qu'il a à faire.
 */
export function BrandLogoBloc({ url, nom }: BrandLogoBlocProps) {
  const adresse = resolveBrandAssetUrl(url);
  const inexploitable = isBrandAssetUrlUnusable(url);
  const { imgRef, enEchec, signalerEchec } = useImageEnEchec(adresse);

  if (adresse && !enEchec) {
    return (
      // Fond blanc : la plupart des logos sont pensés pour un fond clair, et les
      // mails générés sont sur fond blanc. On montre donc le rendu réel.
      // eslint-disable-next-line @next/next/no-img-element
      <img
        ref={imgRef}
        src={adresse}
        alt={`Logo ${nom}`}
        onError={signalerEchec}
        className="max-h-[120px] max-w-[280px] object-contain bg-white rounded p-3"
      />
    );
  }

  const motif = inexploitable
    ? "L'adresse enregistrée pour ce logo est inexploitable."
    : "Ce logo est introuvable sur le serveur.";

  return (
    <div className="flex flex-col items-center gap-1.5 rounded-lg border border-[rgba(251,191,36,0.28)] bg-[rgba(251,191,36,0.07)] px-4 py-3 text-center">
      <WarningCircle size={22} className="text-[#FBBF24]" />
      <p className="text-[12px] font-medium text-[#FBBF24]">{motif}</p>
      <p className="text-[10.5px] leading-[1.45] text-[#C4C7CC] max-w-[320px]">
        Le fichier a pu être supprimé du serveur. Envoyez-le à nouveau pour
        rétablir l&apos;habillage des mails et des factures de cette société.
      </p>
    </div>
  );
}
